export const ISSUE_SEVERITIES = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
  SYSTEM: 'system',
};

/**
 * Rule types raised by this context rather than by a field's own validators.
 *
 * A field rule's type comes from `VALIDATORS`; these are the judgements about a whole entity, and
 * they were string literals in the service and again in its tests — so renaming one left six
 * assertions passing against a rule nothing raised any more. Only the ones a caller outside
 * `validation.js` has to name are here; the rest stay local to the rule that raises them.
 */
export const VALIDATION_RULES = {
  FILE_DUPLICATES: 'fileDuplicates',
  PROGRAM_WILL_BE_OVERWRITTEN: 'programWillBeOverwritten',
  PROGRAM_NAME_AMBIGUOUS: 'programNameAmbiguous',
  PROMPT_LEVEL_TEMPLATE_UNAVAILABLE: 'promptLevelTemplateUnavailable',
  TARGET_DATE_CONTRADICTS_STATUS: 'targetDateContradictsStatus',
  TARGET_DATE_MISSING_FOR_STATUS: 'targetDateMissingForStatus',
  TARGET_DATA_TYPE_NOT_IN_PROGRAM_TYPE: 'targetDataTypeNotInProgramType',
  TARGET_CHILDREN_IN_WRONG_COLUMN: 'targetChildrenInWrongColumn',
  TARGET_TYPE_LISTS_NO_CHILDREN: 'targetTypeListsNoChildren',
  TARGET_CHILDREN_NEED_TARGET_TYPE: 'targetChildrenNeedTargetType',
  TARGET_METRIC_NOT_FOR_DATA_TYPE: 'targetMetricNotForDataType',
  TARGET_FIELD_NOT_ON_DATA_TYPE: 'targetFieldNotOnDataType',
  TARGET_WORKFLOW_NOT_FOR_METRIC: 'targetWorkflowNotForMetric',
};

/**
 * What each severity is called and how it sorts.
 *
 * NO ICON AND NO COLOUR. Those used to live here — a Material icon name and a Quasar colour token,
 * in a domain constant. `HrSeverityLabel` decides both from the severity now, and
 * `importIssuePresenter` translates these five onto the four it speaks. A domain layer that knows
 * what colour `critical` is has learned something it cannot use.
 */
export const ISSUE_SEVERITY_META = {
  [ISSUE_SEVERITIES.INFO]: {
    label: 'common.issueSeverities.info',
    order: 40,
  },
  [ISSUE_SEVERITIES.WARNING]: {
    label: 'common.issueSeverities.warning',
    order: 30,
  },
  [ISSUE_SEVERITIES.ERROR]: {
    label: 'common.issueSeverities.error',
    order: 20,
  },
  [ISSUE_SEVERITIES.CRITICAL]: {
    label: 'common.issueSeverities.critical',
    order: 10,
  },
  [ISSUE_SEVERITIES.SYSTEM]: {
    label: 'common.issueSeverities.system',
    order: 0,
  },
};

export const IMPORT_TYPES = {
  PROGRAM: 'program',
  TARGET: 'programTarget',
  SESSION: 'session',
  SESSION_PROGRAM: 'sessionProgram',
  SESSION_DATA: 'sessionData',
  CLIENT: 'client',
  USER: 'user',
  STAFF_MEMBER: 'staffMember',
};

/**
 * Which entity types carry children, where they carry them, and what those children are.
 *
 * STATED ONCE BECAUSE IT WAS STATED THREE TIMES. Validation walked children to validate them,
 * normalization walked them to normalize them, and the preview table rendered a nested table each,
 * every one a hand-written branch per pair. Nothing compared them, and they had already drifted:
 * one walk checked a collection the row does not have, and three of the five rows offered to expand
 * whether or not anything was inside.
 *
 * A sixth nested type is now one entry rather than three edits, and the one it is easy to forget —
 * the rendering — is the one that reads from here.
 */
export const NESTED_ENTITY_TYPES = {
  [IMPORT_TYPES.PROGRAM]: { collection: 'programTargets', childType: IMPORT_TYPES.TARGET },
  [IMPORT_TYPES.TARGET]: { collection: 'subTargets', childType: IMPORT_TYPES.TARGET },
  [IMPORT_TYPES.SESSION]: { collection: 'sessionPrograms', childType: IMPORT_TYPES.SESSION_PROGRAM },
  [IMPORT_TYPES.SESSION_PROGRAM]: { collection: 'sessionData', childType: IMPORT_TYPES.SESSION_DATA },
  [IMPORT_TYPES.USER]: { collection: 'staffMember', childType: IMPORT_TYPES.STAFF_MEMBER },
};

/**
 * The children an entity actually carries, or an empty list.
 *
 * @param {{ type?: string }} entity
 * @returns {{ collection: (string|null), children: Array }}
 */
export function nestedEntitiesOf(entity) {
  const nesting = NESTED_ENTITY_TYPES[entity?.type];
  const children = nesting ? entity?.[nesting.collection] : null;
  return {
    collection: nesting?.collection ?? null,
    children: Array.isArray(children) ? children : [],
  };
} // nestedEntitiesOf()

export const ENTITY_TYPE_LABEL_KEYS = {
  [IMPORT_TYPES.PROGRAM]: 'importPipeline.entityTypeLabels.program',
  [IMPORT_TYPES.TARGET]: 'importPipeline.entityTypeLabels.programTarget',
  [IMPORT_TYPES.SESSION]: 'importPipeline.entityTypeLabels.session',
  [IMPORT_TYPES.SESSION_PROGRAM]: 'importPipeline.entityTypeLabels.sessionProgram',
  [IMPORT_TYPES.SESSION_DATA]: 'importPipeline.entityTypeLabels.sessionData',
  [IMPORT_TYPES.CLIENT]: 'importPipeline.entityTypeLabels.client',
  [IMPORT_TYPES.USER]: 'importPipeline.entityTypeLabels.user',
  [IMPORT_TYPES.STAFF_MEMBER]: 'importPipeline.entityTypeLabels.staffMember',
};

export const IMPORT_SOURCE_TYPES = {
  STATIC_VALUE: 'staticValue',
  SOURCE_FILE: 'sourceFile',
};

export const FIELD_BINDING_TYPES = {
  COLUMN: 'column',
  STATIC_VALUE: 'staticValue',
};

export const FIELD_BINDING_PROVENANCES = {
  ADAPTER: 'adapter',
  USER: 'user',
};

/**
 * What separates the values of one multi-value cell. Not a comma: the values are a clinic's own
 * words, and those contain commas often enough that splitting on one breaks a value into pieces that
 * match nothing.
 */
export const MULTI_VALUE_SEPARATOR = '|';

export const NORMALIZERS = {
  TRIM: 'trim',
  ENUM: 'enum',
  ENUM_SET_RAW: 'enumSetRaw',
  INTEGER: 'integer',
  DECIMAL: 'decimal',
  PERCENTAGE: 'percentage',
  STRIP_NON_NUMERIC: 'stripNonNumeric',
  EXCEL_DATE: 'excelDate',
  EXCEL_DATETIME: 'excelDatetime',
  EXCEL_DURATION: 'excelDuration',
  PROGRAM: 'program',
  TARGET: 'target',
  PROMPT_LEVEL_TEMPLATE: 'promptLevelTemplate',
  WORKFLOW: 'workflow',
  TREATMENT_AREA: 'treatmentArea',
  CUSTOM: 'custom',
  UNITS: 'units',
  USER: 'user',
  COUNTRY: 'country',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  NO_FUTURE_DATE: 'noFutureDate',
};

export const FORMATTERS = {
  TEXT: 'text',
  NUMBER: 'number',
  PERCENTAGE: 'percentage',
  DURATION: 'duration',
  YESNO: 'yesNo',
  ENUM: 'enum',
  ENUM_SET_RAW: 'enumSetRaw',
  DATE: 'date',
  DATETIME: 'datetime',
  PROGRAM: 'program',
  TARGET: 'target',
  PROMPT_LEVEL_TEMPLATE: 'promptLevelTemplate',
  WORKFLOW: 'workflow',
  TREATMENT_AREA: 'treatmentArea',
  UNITS: 'units',
  USER: 'user',
  COUNTRY: 'country',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  STATUS: 'status',
  NO_FUTURE_DATE: 'noFutureDate',
  MFASETUP: 'mfaSetUp',
};

export const NORMALIZATION_ISSUES = {
  INVALID_DECIMAL_VALUE: 'invalidDecimalValue',
  INVALID_CUSTOM_FUNCTION: 'invalidCustomFunction',
  INVALID_DATE_FORMAT: 'invalidDateFormat',
  INVALID_INTEGER_VALUE: 'invalidIntegerValue',
  INVALID_PERCENTAGE_VALUE: 'invalidPercentageValue',
  INVALID_ENUM_VALUE: 'invalidEnumValue',
  INVALID_DURATION_FORMAT: 'invalidDurationFormat',
  INVALID_PROGRAM_NAME: 'invalidProgramName',
  INVALID_TARGET_NAME: 'invalidTargetName',
  INVALID_PROMPT_LEVEL_TEMPLATE_NAME: 'invalidPromptLevelTemplateName',
  INVALID_WORKFLOW_NAME: 'invalidWorkflowName',
  INVALID_TREATMENT_AREA_NAME: 'invalidTreatmentAreaName',
  INVALID_UNIT_NAME: 'invalidUnitName',
  INVALID_USER_NAME: 'invalidUserName',
  INVALID_COUNTRY: 'invalidCountry',
  INVALID_STATE: 'invalidState',
  INVALID_POSTAL_CODE: 'invalidPostalCode',
  FUTURE_DATE: 'futureDate',
};

export const VALIDATORS = {
  UNKNOWN_RULE: 'unknownRule',
  REQUIRED_FIELD: 'requiredField',
  TEXT: 'text',
  TEXT_MIN_LENGTH: 'textMinLength',
  TEXT_MAX_LENGTH: 'textMaxLength',
  NUMBER: 'number',
  NUMBER_MIN: 'numberMin',
  NUMBER_MAX: 'numberMax',
  NUMBER_ONE_OF: 'numberOneOf',
  PERCENTAGE: 'percentage',
  PERCENTAGE_MIN: 'percentageMin',
  PERCENTAGE_MAX: 'percentageMax',
  BOOLEAN: 'boolean',
  ENUM: 'enum',
  ENUM_SET: 'enumSet',
  DATE: 'date',
  PROGRAM: 'program',
  TARGET: 'target',
  PROMPT_LEVEL_TEMPLATE: 'promptLevelTemplate',
  WORKFLOW: 'workflow',
  TREATMENT_AREA: 'treatmentArea',
  UNITS: 'units',
  USER: 'user',
  EMAIL: 'email',
  COUNTRY: 'country',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  NO_FUTURE_DATE: 'noFutureDate',
};

export const PARSE_ISSUE_CODES = {
  INTERPRET_ERROR: 'interpretError',
  MISSING_REQUIRED_FIELD: 'missingRequiredField',
  UNKNOWN_COLUMN: 'unknownColumn',
};

/**
 * Mapping of user-facing enum labels to internal enum values for various importable
 * fields. This is used during normalization to convert user-provided values into the
 * expected internal representations, validation and display of enum values.
 */
/**
 * NOTE ON WHAT IS AND IS NOT HERE. Programs and targets are absent from the three tables below:
 * Treatment Planning declares its own importable fields, and this context translates them — see
 * `domain/services/fieldDeclaration` and the ImportTarget port. Clients, users and sessions are
 * still declared here because the contexts that own them do not exist yet. That is a fact about how
 * far the migration has got, not a difference in kind.
 *
 * `IMPORT_ENUM_SPELLING` keeps every entity type INCLUDING programs and targets, and always will:
 * how a spreadsheet spells a value is this context's anti-corruption layer, not the owning
 * context's business.
 */

export const FIELD_ENUM_MAPPING = {
  [IMPORT_TYPES.CLIENT]: {
    gender: {
      male: 'clients.modals.editClient.gender.male',
      female: 'clients.modals.editClient.gender.female',
      other: 'clients.modals.editClient.gender.other',
    },

    // For future implementations. For now, we are importing clients with a default 'Active' status
    status: {
      inactive: 'clients.status.inactive',
      active: 'clients.status.active',
    },
  },

  [IMPORT_TYPES.USER]: {
    role: {
      manager: 'common.userRoles.manager',
      administrator: 'common.userRoles.administrator',
      supervisor: 'common.userRoles.supervisor',
      senior_therapist: 'common.userRoles.senior_therapist',
      trainer: 'common.userRoles.trainer',
      parent: 'common.userRoles.parent',
    },
    loginMethod: {
      PASSWORD: 'login.inputPasswordLabel',
      GOOGLE: 'Google',
      APPLE: 'Apple',
      MICROSOFT: 'Microsoft',
    },
  },
  [IMPORT_TYPES.STAFF_MEMBER]: {
    status: {
      active: 'staff.status.active',
      inactive: 'staff.status.inactive',
    },
    bacbLevel: {
      BCBAD: 'BCBA-D',
      BCBA: 'BCBA',
      BCaBA: 'BCaBA',
      RBT: 'RBT',
    },
    ibaoLevel: {
      IBA: 'IBA',
      IBT: 'IBT',
    },
    qabaLevel: {
      QBA: 'QBA',
      QASPS: 'QASP-S',
      ABAT: 'ABAT',
    },
  },
};

/**
 * How each enum value is SPELLED IN AN IMPORT FILE.
 *
 * THE FILE FORMAT IS ENGLISH, AND IT IS CODIFIED HERE RATHER THAN READ FROM i18n. It used to be
 * `i18n.t()` of the label key, resolved at whatever locale the importing user happened to be in, so
 * the accepted spelling changed with the dictionary and differed per user — a Danish user had to
 * type the English header `Program Status` and then the Danish value `sonde` underneath it. Column
 * headers were always hardcoded English (see each adapter's field map); this makes the values agree
 * with them.
 *
 * THE DRIFT FROM `FIELD_ENUM_MAPPING` IS DELIBERATE. That map still holds the label keys, and it is
 * still what the UI renders. If a copy change makes the UI read "Baseline Probe", a file still says
 * `Probe` and that is correct: a file format is a contract with every customer template already on
 * disk, and contracts do not move when copy does. Do not "fix" the two back together.
 *
 * Matching is case-insensitive and whitespace-trimmed, so `  discontinued ` is accepted. The stored
 * value is NOT: `aborted` fails, because only the spelling is part of the format.
 */
const MEASUREMENT_METRIC_SPELLING = {
  percentCorrect: 'Percent Correct',
  frequency: 'Frequency',
  totalDuration: 'Total Observed Duration',
  minDuration: 'Min. Observed Duration',
  maxDuration: 'Max. Observed Duration',
  avgDuration: 'Avg. Observed Duration',
  ratePerMinute: 'Rate per Minute',
  ratePerHour: 'Rate per Hour',
};

export const IMPORT_ENUM_SPELLING = {
  [IMPORT_TYPES.PROGRAM]: {
    // ONLY THE TYPES AN IMPORT MAY CREATE, and a file may say nothing else. The retired spellings
    // were folded into Skill Acquisition for a while, which meant a file describing a Duration
    // program silently got a different one; a program type is not ours to reinterpret on the
    // author's behalf. A blank cell still means Skill Acquisition, because saying nothing is not
    // the same as saying something we cannot honour.
    type: {
      simple: 'Skill Acquisition',
      instructions: 'Instructions Only',
    },
    status: {
      waiting: 'Waiting',
      baseline: 'Baseline',
      active: 'Active',
      maintenance: 'Maintenance',
      hold: 'Hold',
      closed: 'Closed',
      discontinued: 'Discontinued',
    },
    primaryMetric: MEASUREMENT_METRIC_SPELLING,
  },

  [IMPORT_TYPES.TARGET]: {
    status: {
      waiting: 'Waiting',
      probe: 'Probe',
      acquisition: 'Acquisition',
      mastered: 'Mastered',
      closed: 'Closed',
      hold: 'Hold',
      // The one spelling that is not its value title-cased. `aborted` is not accepted.
      aborted: 'Discontinued',
    },
    // A target overriding its program's metric spells it the same way the program does.
    primaryMetric: MEASUREMENT_METRIC_SPELLING,
    dataType: {
      discreteTrial: 'Discrete Trial',
      targetSet: 'Set of Targets',
      taskAnalysis: 'Task Analysis',
      duration: 'Duration',
      rate: 'Rate',
      frequency: 'Frequency',
      shaping: 'Shaping',
      randomRotation: 'Random Rotation',
      cabBranch: 'CAB Branch',
      instructions: 'Instructions',
      interval: 'Interval',
    },
  },

  [IMPORT_TYPES.CLIENT]: {
    gender: { male: 'Male', female: 'Female', other: 'Other' },
  },

  [IMPORT_TYPES.USER]: {
    // The stored role and its label diverge for two of these, so transcribing the values would be
    // wrong: `senior_therapist` is spelled "Behavior Analyst" and `trainer` "Behavior Technician".
    role: {
      manager: 'Manager',
      administrator: 'Administrator',
      supervisor: 'Supervisor',
      senior_therapist: 'Behavior Analyst',
      trainer: 'Behavior Technician',
      parent: 'Parent',
    },
    // Keyed by the FIELD name, not by the name FIELD_ENUM_MAPPING happens to use for the same
    // vocabulary (`loginMethod`). The lookup is by field key, so the two must agree here.
    requireLoginMethod: {
      PASSWORD: 'Password',
      GOOGLE: 'Google',
      APPLE: 'Apple',
      MICROSOFT: 'Microsoft',
    },
  },

  [IMPORT_TYPES.STAFF_MEMBER]: {
    gender: { male: 'Male', female: 'Female', other: 'Other' },
    // A staff member's `status` is their subscription license, not a status: the column is
    // "Staff Profile: License Type" and every staff surface reads Premium or Standard. The stored
    // `active`/`inactive` are not accepted, and never were.
    status: { active: 'Premium', inactive: 'Standard' },
    bacbLevel: { BCBAD: 'BCBA-D', BCBA: 'BCBA', BCaBA: 'BCaBA', RBT: 'RBT' },
    ibaoLevel: { IBA: 'IBA', IBT: 'IBT' },
    qabaLevel: { QBA: 'QBA', QASPS: 'QASP-S', ABAT: 'ABAT' },
  },
};

export const STATIC_VALIDATION_RULES = {
  [IMPORT_TYPES.CLIENT]: {
    clientName: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 510 },
    ],
    firstName: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MIN_LENGTH, minLength: 1 },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    lastName: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MIN_LENGTH, minLength: 1 },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    alias: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 5 },
    ],
    reference: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    gender: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT].gender),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT].gender),
      },
    ],
    birthDate: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.NO_FUTURE_DATE },
    ],
    email: [
      { type: VALIDATORS.EMAIL },
    ],
    phone: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    address1: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    address2: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    postalCode: [
      { type: VALIDATORS.POSTAL_CODE },
    ],
    city: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    state: [
      { type: VALIDATORS.STATE },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 3 },
    ],
    country: [
      { type: VALIDATORS.COUNTRY },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 2 },
    ],
    units: [
      { type: VALIDATORS.UNITS },
    ],
  },

  [IMPORT_TYPES.SESSION]: {
    runTimestamp: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.DATE },
    ],
    // Required because bulkSessionsCreation refuses a session without one, and without this rule the
    // file only failed at save time with a generic error.
    duration: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.NUMBER },
      { type: VALIDATORS.NUMBER_MIN, min: 1 },
      { type: VALIDATORS.NUMBER_MAX, max: 12 * 60 * 60 }, // Max session duration of 12 hours
    ],
    runByName: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    sessionName: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
  },
  [IMPORT_TYPES.SESSION_PROGRAM]: {
    program: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.PROGRAM },
    ],
  },
  [IMPORT_TYPES.SESSION_DATA]: {
    target: [
      { type: VALIDATORS.REQUIRED_FIELD },
    ],
    scoreValue: [
      { type: VALIDATORS.PERCENTAGE },
      { type: VALIDATORS.PERCENTAGE_MIN, min: 0 },
      { type: VALIDATORS.PERCENTAGE_MAX, max: 100 },
    ],
    duration: [
      { type: VALIDATORS.NUMBER },
      { type: VALIDATORS.NUMBER_MIN, min: 0 },
      { type: VALIDATORS.NUMBER_MAX, max: 12 * 60 * 60 }, // Max observation duration of 12 hours
    ],
  },

  [IMPORT_TYPES.USER]: {
    userName: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MIN_LENGTH, minLength: 1 },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    email: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.EMAIL },
    ],
    role: [
      { type: VALIDATORS.REQUIRED_FIELD },
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.USER].role),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.USER].role),
      },
    ],
    units: [
      { type: VALIDATORS.UNITS },
    ],
    mfaRequired: [
      { type: VALIDATORS.BOOLEAN },
    ],
    requireLoginMethod: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.USER].loginMethod),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.USER].loginMethod),
      },
    ],
  },
  [IMPORT_TYPES.STAFF_MEMBER]: {
    clientName: [
      { type: VALIDATORS.REQUIRED_FIELD },
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 510 },
    ],
    firstName: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MIN_LENGTH, minLength: 1 },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    lastName: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MIN_LENGTH, minLength: 1 },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    reference: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    gender: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT].gender),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT].gender),
      },
    ],
    phone: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    address1: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    address2: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    postalCode: [
      { type: VALIDATORS.POSTAL_CODE },
    ],
    city: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    state: [
      { type: VALIDATORS.STATE },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 3 },
    ],
    country: [
      { type: VALIDATORS.COUNTRY },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 2 },
    ],
    units: [
      { type: VALIDATORS.UNITS },
    ],
    employeeStartDate: [
      { type: VALIDATORS.DATE },
    ],
    employeeEndDate: [
      { type: VALIDATORS.DATE },
    ],
    status: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].status),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].status),
      },
    ],
    bacbCertNumber: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    bacbLevel: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].bacbLevel),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].bacbLevel),
      },
    ],
    bacbCertExpirationDate: [
      { type: VALIDATORS.DATE },
    ],
    nppesNpiNPI: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    nppesNpiTaxonomy: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    lbaUSCertNumber: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    lbaUSCertExpirationDate: [
      { type: VALIDATORS.DATE },
    ],
    ibaoCertNumber: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    ibaoLevel: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].ibaoLevel),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].ibaoLevel),
      },
    ],
    ibaoCertExpirationDate: [
      { type: VALIDATORS.DATE },
    ],
    qabaCertNumber: [
      { type: VALIDATORS.TEXT },
      { type: VALIDATORS.TEXT_MAX_LENGTH, maxLength: 255 },
    ],
    qabaLevel: [
      {
        type: VALIDATORS.ENUM,
        allowedValues: Object.keys(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].qabaLevel),
        allowedValueLabelKeys: Object.values(FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER].qabaLevel),
      },
    ],
    qabaCertExpirationDate: [
      { type: VALIDATORS.DATE },
    ],
  },
};

export const STATIC_IMPORTABLE_FIELDS = {
  [IMPORT_TYPES.CLIENT]: {
    clientName: {
      labelKey: 'importPipeline.fields.client.clientName',
      formatter: FORMATTERS.TEXT,
    },
    firstName: {
      labelKey: 'importPipeline.fields.client.firstName',
      formatter: FORMATTERS.TEXT,
    },
    lastName: {
      labelKey: 'importPipeline.fields.client.lastName',
      formatter: FORMATTERS.TEXT,
    },
    alias: {
      labelKey: 'importPipeline.fields.client.alias',
      formatter: FORMATTERS.TEXT,
    },
    reference: {
      labelKey: 'importPipeline.fields.client.reference',
      formatter: FORMATTERS.TEXT,
    },
    gender: {
      labelKey: 'importPipeline.fields.client.gender',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT]?.gender || {},
    },
    birthDate: {
      labelKey: 'importPipeline.fields.client.birthDate',
      formatter: FORMATTERS.NO_FUTURE_DATE,
    },
    email: {
      labelKey: 'importPipeline.fields.client.email',
      formatter: FORMATTERS.TEXT,
    },
    phone: {
      labelKey: 'importPipeline.fields.client.phone',
      formatter: FORMATTERS.TEXT,
    },
    address1: {
      labelKey: 'importPipeline.fields.client.address1',
      formatter: FORMATTERS.TEXT,
    },
    address2: {
      labelKey: 'importPipeline.fields.client.address2',
      formatter: FORMATTERS.TEXT,
    },
    postalCode: {
      labelKey: 'importPipeline.fields.client.postalCode',
      formatter: FORMATTERS.POSTAL_CODE,
    },
    city: {
      labelKey: 'importPipeline.fields.client.city',
      formatter: FORMATTERS.TEXT,
    },
    state: {
      labelKey: 'importPipeline.fields.client.state',
      formatter: FORMATTERS.STATE,
    },
    country: {
      labelKey: 'importPipeline.fields.client.country',
      formatter: FORMATTERS.COUNTRY,
    },
    units: {
      labelKey: 'importPipeline.fields.client.units',
      formatter: FORMATTERS.UNITS,
    },
    status: {
      labelKey: 'importPipeline.fields.client.status',
      formatter: FORMATTERS.STATUS,
    },
  },
  [IMPORT_TYPES.SESSION]: {
    runTimestamp: {
      labelKey: 'importPipeline.fields.session.runTimestamp',
      formatter: FORMATTERS.DATETIME,
    },
    duration: {
      labelKey: 'importPipeline.fields.session.duration',
      formatter: FORMATTERS.DURATION,
    },
    runByName: {
      labelKey: 'importPipeline.fields.session.runByName',
      formatter: FORMATTERS.TEXT,
    },
    sessionName: {
      labelKey: 'importPipeline.fields.session.sessionName',
      formatter: FORMATTERS.TEXT,
    },
  },
  [IMPORT_TYPES.SESSION_PROGRAM]: {
    program: {
      labelKey: 'importPipeline.fields.sessionProgram.program',
      formatter: FORMATTERS.PROGRAM,
    },
  },
  [IMPORT_TYPES.SESSION_DATA]: {
    target: {
      labelKey: 'importPipeline.fields.sessionData.target',
      formatter: FORMATTERS.TARGET,
    },
    parentTarget: {
      labelKey: 'importPipeline.fields.sessionData.parentTarget',
      formatter: FORMATTERS.TARGET,
    },
    scoreValue: {
      labelKey: 'importPipeline.fields.sessionData.scoreValue',
      formatter: FORMATTERS.PERCENTAGE,
    },
    duration: {
      labelKey: 'importPipeline.fields.sessionData.duration',
      formatter: FORMATTERS.DURATION,
    },
  },
  [IMPORT_TYPES.USER]: {
    userName: {
      labelKey: 'importPipeline.fields.user.userName',
      formatter: FORMATTERS.TEXT,
    },
    email: {
      labelKey: 'importPipeline.fields.user.email',
      formatter: FORMATTERS.TEXT,
    },
    role: {
      labelKey: 'importPipeline.fields.user.role',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.USER]?.role || {},
    },
    units: {
      labelKey: 'importPipeline.fields.user.units',
      formatter: FORMATTERS.UNITS,
    },
    status: {
      labelKey: 'importPipeline.fields.user.status',
      formatter: FORMATTERS.STATUS,
    },
    mfaRequired: {
      labelKey: 'importPipeline.fields.user.mfaRequired',
      formatter: FORMATTERS.YESNO,
    },
    mfaSetUp: {
      labelKey: 'importPipeline.fields.user.mfaSetUp',
      formatter: FORMATTERS.MFASETUP,
    },
    requireLoginMethod: {
      labelKey: 'importPipeline.fields.user.requireLoginMethod',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.USER]?.loginMethod || {},
    },
  },
  [IMPORT_TYPES.STAFF_MEMBER]: {
    clientName: {
      labelKey: 'importPipeline.fields.staff.clientName',
      formatter: FORMATTERS.TEXT,
    },
    firstName: {
      labelKey: 'importPipeline.fields.staff.firstName',
      formatter: FORMATTERS.TEXT,
    },
    lastName: {
      labelKey: 'importPipeline.fields.staff.lastName',
      formatter: FORMATTERS.TEXT,
    },
    reference: {
      labelKey: 'importPipeline.fields.staff.reference',
      formatter: FORMATTERS.TEXT,
    },
    gender: {
      labelKey: 'importPipeline.fields.staff.gender',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.CLIENT]?.gender || {},
    },
    phone: {
      labelKey: 'importPipeline.fields.staff.phone',
      formatter: FORMATTERS.TEXT,
    },
    address1: {
      labelKey: 'importPipeline.fields.staff.address1',
      formatter: FORMATTERS.TEXT,
    },
    address2: {
      labelKey: 'importPipeline.fields.staff.address2',
      formatter: FORMATTERS.TEXT,
    },
    postalCode: {
      labelKey: 'importPipeline.fields.staff.postalCode',
      formatter: FORMATTERS.POSTAL_CODE,
    },
    city: {
      labelKey: 'importPipeline.fields.staff.city',
      formatter: FORMATTERS.TEXT,
    },
    state: {
      labelKey: 'importPipeline.fields.staff.state',
      formatter: FORMATTERS.STATE,
    },
    country: {
      labelKey: 'importPipeline.fields.staff.country',
      formatter: FORMATTERS.COUNTRY,
    },
    units: {
      labelKey: 'importPipeline.fields.staff.units',
      formatter: FORMATTERS.UNITS,
    },
    employeeStartDate: {
      labelKey: 'importPipeline.fields.staff.employeeStartDate',
      formatter: FORMATTERS.DATE,
    },
    employeeEndDate: {
      labelKey: 'importPipeline.fields.staff.employeeEndDate',
      formatter: FORMATTERS.DATE,
    },
    status: {
      labelKey: 'importPipeline.fields.staff.status',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER]?.status || {},
    },
    bacbCertNumber: {
      labelKey: 'importPipeline.fields.staff.bacbCertNumber',
      formatter: FORMATTERS.TEXT,
    },
    bacbLevel: {
      labelKey: 'importPipeline.fields.staff.bacbLevel',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER]?.bacbLevel || {},
    },
    bacbCertExpirationDate: {
      labelKey: 'importPipeline.fields.staff.bacbCertExpirationDate',
      formatter: FORMATTERS.DATE,
    },
    nppesNpiNPI: {
      labelKey: 'importPipeline.fields.staff.nppesNpiNPI',
      formatter: FORMATTERS.TEXT,
    },
    nppesNpiTaxonomy: {
      labelKey: 'importPipeline.fields.staff.nppesNpiTaxonomy',
      formatter: FORMATTERS.TEXT,
    },
    lbaUSCertNumber: {
      labelKey: 'importPipeline.fields.staff.lbaUSCertNumber',
      formatter: FORMATTERS.TEXT,
    },
    lbaUSCertExpirationDate: {
      labelKey: 'importPipeline.fields.staff.lbaUSCertExpirationDate',
      formatter: FORMATTERS.DATE,
    },
    ibaoCertNumber: {
      labelKey: 'importPipeline.fields.staff.ibaoCertNumber',
      formatter: FORMATTERS.TEXT,
    },
    ibaoLevel: {
      labelKey: 'importPipeline.fields.staff.ibaoLevel',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER]?.ibaoLevel || {},
    },
    ibaoCertExpirationDate: {
      labelKey: 'importPipeline.fields.staff.ibaoCertExpirationDate',
      formatter: FORMATTERS.DATE,
    },
    qabaCertNumber: {
      labelKey: 'importPipeline.fields.staff.qabaCertNumber',
      formatter: FORMATTERS.TEXT,
    },
    qabaLevel: {
      labelKey: 'importPipeline.fields.staff.qabaLevel',
      formatter: FORMATTERS.ENUM,
      enumValueLabelKeyMap: FIELD_ENUM_MAPPING[IMPORT_TYPES.STAFF_MEMBER]?.qabaLevel || {},
    },
    qabaCertExpirationDate: {
      labelKey: 'importPipeline.fields.staff.qabaCertExpirationDate',
      formatter: FORMATTERS.DATE,
    },
  },
};
